import { LoginCallback, Security } from '@okta/okta-react';
import { Route, BrowserRouter, Routes, useLocation, useNavigate } from 'react-router-dom';
    import { StyledEngineProvider, ThemeProvider, Box, createTheme } from '@mui/material';
import { createContext, lazy, useMemo, useState } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ColorMode } from './components/ThemeToggle';
import 'react-toastify/dist/ReactToastify.css';
import RootLayout from './components/layouts/RootLayout';
import { ToastContainer } from 'react-toastify';
import './global.css';
import Unauthorized from './pages/Unauthorized';
import { MessageContextProvider } from './context/MessageContext';
import { SettingsContext, SettingsContextProvider } from './context/SettingsContext';
import { Roles } from './enum/Roles';
import { getAppFromPath } from './enum/UrlAppMap';
// Import shared auth configuration
import {
  oktaAuthConfig,
  IdentityContextProvider,
  withAuthorization,
  ProtectedRoute,
  useCreateRestoreOriginalUri,
} from './config/authConfig';
// Import custom hooks
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { useRouteRedirect } from './hooks/useRouteRedirect';
// Import pages
import HelpPage from './pages/HelpPage';
import ChatPage from './pages/ChatPage';
import { ProductFinderPage } from './pages/ProductFinderPage';
import Users from './pages/Users';
import CustomRankings from './pages/CustomRankings';
import Etos from './pages/Etos';
import SetonPipeMarkersPage from './pages/SetonPipeMarkersPage';
import HomePage from './pages/HomePage';
import { bradyThemeOptions, injectBradyFonts } from '@brady/react-core';
import { themeOptions } from './theme';

injectBradyFonts();
const ColorModeContext = createContext({
  toggleColorMode: () => {},
  mode: ColorMode.Light,
});

const AuthorizedEditUsers = withAuthorization(Users, [Roles.Admin]);
const AuthorizedChatPage = withAuthorization(ChatPage, [Roles.EsaUser, Roles.EsaAdmin, Roles.Admin]);
const AuthorizedHelpPage = withAuthorization(HelpPage, [Roles.User, Roles.Admin]);
const AuthorizedHomePage = withAuthorization(HomePage, [Roles.User, Roles.Admin]);
const AuthorizedProductFinderPage = withAuthorization(ProductFinderPage, [
  Roles.ProductFinderUser,
  Roles.ProductFinderAdmin,
  Roles.Admin,
]);
const AuthorizedSetonPipeMarkersPage = withAuthorization(SetonPipeMarkersPage, [
  Roles.TakeOffUser,
  Roles.TakeOffAdmin,
  Roles.Admin,
]);
const DistributorSyndication = lazy(() => import('./pages/DistributorSyndication'));
const AuthorizedDistributorSyndication = withAuthorization(DistributorSyndication, [
  Roles.DistSyndicationUser,
  Roles.DistSyndicationAdmin,
  Roles.Admin,
]);
const AuthorizedEditCustomRankings = withAuthorization(CustomRankings, [Roles.ProductFinderAdmin, Roles.Admin]);
const AuthorizedEtos = withAuthorization(Etos, [Roles.ProductFinderAdmin, Roles.Admin]);

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<ColorMode>(ColorMode.Light);
  const [scrollToNewest, setScrollToNewest] = useState(true);
  const selectedApp = getAppFromPath(location.pathname);
  const restoreOriginalUri = useCreateRestoreOriginalUri();

  // Use custom hooks for route management
  useRouteRedirect();
  useDocumentTitle();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === ColorMode.Light ? ColorMode.Dark : ColorMode.Light));
      },
      mode,
    }),
    [mode]
  );

  const theme = createTheme(bradyThemeOptions, themeOptions);

  return (
    <Security oktaAuth={oktaAuthConfig} restoreOriginalUri={restoreOriginalUri}>
      <IdentityContextProvider>
        <MessageContextProvider>
          <ThemeProvider theme={theme}>
            <StyledEngineProvider injectFirst>
              <ColorModeContext.Provider value={colorMode}>
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <Box className="App">
                    <Routes>
                      <Route
                        path="/login/callback"
                        element={
                          <LoginCallback
                            errorComponent={(err) => {
                              console.error(err);
                              navigate('/unauthorized');
                              return null;
                            }}
                          />
                        }
                      />
                      <Route path={'/unauthorized'} element={<Unauthorized />} />
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute
                            component={RootLayout}
                            {...{
                              setScrollToNewest,
                              selectedApp,
                            }}
                          />
                        }
                      >
                        <Route path="/" element={<AuthorizedHomePage />} />
                        <Route path="/home" element={<AuthorizedHomePage />} />
                        <Route
                          path="chat"
                          element={
                            <AuthorizedChatPage scrollToNewest={scrollToNewest} setScrollToNewest={setScrollToNewest} />
                          }
                        />
                        <Route path="help" element={<AuthorizedHelpPage />} />
                        <Route path="users" element={<AuthorizedEditUsers />} />
                        <Route path="product-finder" element={<AuthorizedProductFinderPage />} />
                        <Route path="product-finder/custom-rankings" element={<AuthorizedEditCustomRankings />} />
                        <Route path="product-finder/etos" element={<AuthorizedEtos />} />
                        <Route path="seton-pipe-markers" element={<AuthorizedSetonPipeMarkersPage />} />
                        <Route path="distributor-syndication" element={<AuthorizedDistributorSyndication />} />
                      </Route>
                    </Routes>
                  </Box>
                </ThemeProvider>
            </ColorModeContext.Provider>
          </StyledEngineProvider>
          </ThemeProvider>
        </MessageContextProvider>
      </IdentityContextProvider>
    </Security>
  );
};

const AppWrapper = () => {
  return (
    <BrowserRouter>
      <SettingsContextProvider>
        <ToastContainer autoClose={3000} hideProgressBar={true} />
        <SettingsContext.Consumer>{() => <App />}</SettingsContext.Consumer>
      </SettingsContextProvider>
    </BrowserRouter>
  );
};

export default AppWrapper;
